/**
 * TRAC-1212: record every time the chat window is opened on a host that embeds it as a
 * plain iframe (tirerack.com today), not only the first open of a browser session.
 *
 * How the host behaves (from its own script, captured in frontend/public/tr-pdp-files):
 * its button CREATES the chat iframe on the first open of a page view; closing (its
 * button again, our minimize, or LivePerson opening) adds a `hide` class to the
 * container; opening again on the same page REMOVES that class. It never tells this
 * frame either way. A new page view starts over with no iframe.
 *
 * So there are two kinds of open session-init never sees:
 *  - 'remount': the iframe was created on a new page view, with a conversation already
 *    in storage. session-init only fires for a FRESH session, so this went unrecorded.
 *    When this load did mint a fresh session, session-init already recorded the open
 *    as agent_session_opened, and nothing is sent here — one open, one record.
 *  - 'reshown': the same iframe was hidden and shown again on the same page.
 *
 * Seeing 'reshown' from inside a cross-origin frame: an IntersectionObserver on our own
 * document. Checked in Chrome (cross-origin, site isolation on): hiding the container
 * with display:none reports not-intersecting with a 0x0 bounding box, and showing it
 * again reports intersecting. Scrolling the frame out of view ALSO reports
 * not-intersecting, but with its real size — so only a 0x0 report counts as closed, and
 * scrolling can never be mistaken for a re-open. No resize or visibilitychange event
 * fires on hide, so neither is used.
 *
 * Inside our own page shell (?shell=1) none of this runs: the shell sees every open
 * itself and records it from the page (agent_opened from loader.js).
 */
export type OpenKind = 'remount' | 'reshown';

export interface FrameSighting {
  isIntersecting: boolean;
  width: number;
  height: number;
}

/**
 * The re-open rule, pure. Returns a function to feed every sighting; calls `onReopen`
 * once per hidden-then-shown cycle. Never throws.
 */
export function createReopenDetector(onReopen: () => void): (s: FrameSighting) => void {
  let hidden = false;
  return (s) => {
    try {
      const collapsed = !s.isIntersecting && s.width === 0 && s.height === 0;
      if (collapsed) {
        hidden = true;
        return;
      }
      if (hidden && s.isIntersecting) {
        hidden = false;
        onReopen();
      }
    } catch {
      /* measurement never breaks the chat */
    }
  };
}

/** The slice of the chat service this needs (RealChatAgentService). */
export interface OpenTrackerService {
  onAgentSessionIdChange(listener: (id: string | null) => void): () => void;
  mintedSessionThisLoad(): boolean;
  recordChatOpened(kind: OpenKind): void;
}

/** How long the mount open waits for a session id before giving up on recording it. */
const SESSION_WAIT_MS = 15_000;

/**
 * Wire it up for one page view. The window mounting IS an open on this host (it is only
 * ever created by an open), so the mount is judged once the session id is known; after
 * that, every hidden-then-shown cycle is another. Returns a cleanup. Never throws.
 */
export function installOpenTracker(win: Window, service: OpenTrackerService): () => void {
  const cleanups: Array<() => void> = [];
  try {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => { settled = true; unsubscribe?.(); }, SESSION_WAIT_MS);
    cleanups.push(() => { clearTimeout(timer); unsubscribe?.(); });
    unsubscribe = service.onAgentSessionIdChange((id) => {
      if (settled || !id) return;
      settled = true;
      clearTimeout(timer);
      // Deferred: the subscription replays an id synchronously, before `unsubscribe` exists.
      setTimeout(() => unsubscribe?.(), 0);
      if (!service.mintedSessionThisLoad()) service.recordChatOpened('remount');
    });

    const IO = (win as unknown as { IntersectionObserver?: typeof IntersectionObserver }).IntersectionObserver;
    const root = win.document?.documentElement;
    if (IO && root) {
      const see = createReopenDetector(() => service.recordChatOpened('reshown'));
      const observer = new IO((entries) => {
        for (const e of entries) {
          see({
            isIntersecting: e.isIntersecting,
            width: e.boundingClientRect.width,
            height: e.boundingClientRect.height,
          });
        }
      }, { threshold: [0] });
      observer.observe(root);
      cleanups.push(() => observer.disconnect());
    }
  } catch {
    /* measurement never breaks the chat */
  }
  return () => {
    for (const c of cleanups) {
      try { c(); } catch { /* ignore */ }
    }
  };
}
